import { addItem, billableItems, newOrder, orderLines, WORK_RANK } from './orders.js';
import { cartTotals } from './sale.js';
/**
 * Serviços (docs/14 Fase 5): números de série e garantias, e ordens de
 * serviço (lavandaria, oficina, reparações). A ordem é uma comanda do tipo
 * `workorder`: sincroniza entre caixas e funciona sem rede; não é documento
 * fiscal. O orçamento é uma pró-forma; a entrega cobra-se numa venda normal.
 */
// ── Números de série e garantias ───────────────────────────────────────────
/** Algarismo de controlo do IMEI (Luhn). */
export function imeiValid(s) {
    if (!/^\d{15}$/.test(s))
        return false;
    let sum = 0;
    for (let i = 0; i < 15; i++) {
        let d = Number(s[14 - i]);
        if (i % 2 === 1) {
            d *= 2;
            if (d > 9)
                d -= 9;
        }
        sum += d;
    }
    return sum % 10 === 0;
}
/** Número de série limpo (maiúsculas, sem espaços); 15 algarismos = IMEI, conferido. */
export function normalizeSerial(raw) {
    const s = raw.trim().toUpperCase().replace(/\s+/g, '');
    if (!/^[A-Z0-9][A-Z0-9\-/.]{3,39}$/.test(s))
        throw new RangeError('Número de série inválido (4 a 40 letras ou algarismos)');
    if (/^\d{15}$/.test(s) && !imeiValid(s))
        throw new RangeError(`IMEI ${s} inválido: o último algarismo não confere`);
    return s;
}
/** Fim da garantia: a data da venda mais N meses (no fim de mês, o último dia do mês de chegada). */
export function warrantyEnd(soldOn, months) {
    if (!months || months < 1)
        return null;
    const y = Number(soldOn.slice(0, 4)), m = Number(soldOn.slice(5, 7)) - 1 + months, d = Number(soldOn.slice(8, 10));
    const ty = y + Math.floor(m / 12), tm = m % 12;
    const last = new Date(Date.UTC(ty, tm + 1, 0)).getUTCDate();
    return `${ty}-${String(tm + 1).padStart(2, '0')}-${String(Math.min(d, last)).padStart(2, '0')}`;
}
/** Problemas dos números de série de uma venda: um por unidade, sem repetidos. */
export function serialProblem(lines) {
    const seen = new Set();
    for (const l of lines) {
        if (!l.serial)
            continue;
        if (String(l.quantity) !== '1')
            return `${l.description}: com número de série, uma unidade por linha`;
        if (seen.has(l.serial))
            return `O número de série ${l.serial} está repetido na venda`;
        seen.add(l.serial);
    }
    return null;
}
// ── Ordens de serviço ──────────────────────────────────────────────────────
export const WORK_LABEL = { received: 'Recebida', in_progress: 'Em curso', ready: 'Pronta', delivered: 'Entregue', cancelled: 'Anulada' };
/** Número seguinte desta caixa: o maior que ela conhece com o mesmo prefixo, mais um. */
export function nextWorkSeq(orders, prefix) {
    return 1 + Math.max(0, ...orders.filter((o) => o.work?.prefix === prefix).map((o) => o.work.seq));
}
const itemOf = (sv, by, staffId) => ({
    id: sv.itemId, productId: sv.productId, description: sv.description, unitPrice: sv.unitPrice, taxRate: sv.taxRate, priceIncludesTax: sv.priceIncludesTax,
    exemptionCode: sv.exemptionCode ?? null, quantity: sv.quantity ?? '1', station: null, addedBy: by, staffId,
    ...(sv.serial ? { serial: sv.serial, warrantyUntil: sv.warrantyUntil ?? null } : {}),
});
/** Recepção: o cliente deixa o objecto; a ordem fica "recebida", com os serviços pedidos (se já se sabem). */
export function newWorkOrder(p) {
    const name = p.customer.name.trim();
    if (!name)
        throw new Error('Indique o nome do cliente');
    if (!p.customer.phone?.trim() && !p.customer.id)
        throw new Error('Indique o telefone do cliente (para avisar quando estiver pronto)');
    const object = p.object.trim().replace(/\s+/g, ' ');
    if (!object)
        throw new Error('Indique o que o cliente deixou');
    if (object.length > 120)
        throw new Error('Descrição do objecto demasiado comprida');
    if (p.problem.trim().length > 500)
        throw new Error('Descrição do pedido demasiado comprida');
    if (p.pieces != null && (!Number.isInteger(p.pieces) || p.pieces < 1 || p.pieces > 200))
        throw new Error('Número de peças inválido (1 a 200)');
    if (p.promisedOn && !/^\d{4}-\d{2}-\d{2}$/.test(p.promisedOn))
        throw new Error('Data prometida inválida');
    const number = `${p.prefix}-${p.seq}`;
    const at = p.now.toISOString();
    const o = newOrder({ id: p.id, kind: 'workorder', label: `OS ${number} · ${name}`, operatorId: p.operatorId, terminalId: p.terminalId, now: p.now });
    const work = {
        number, prefix: p.prefix, seq: p.seq, customer: { ...p.customer, name, phone: p.customer.phone?.trim() || null },
        object, identifier: p.identifier?.trim() ? p.identifier.trim().toUpperCase() : null, problem: p.problem.trim(), notes: null,
        promisedOn: p.promisedOn ?? null, pieces: p.pieces ?? null, technicianId: p.technicianId ?? null,
        status: 'received', history: [{ status: 'received', at, by: p.operatorId }], ...(p.warranty ? { warranty: p.warranty } : {}),
    };
    return (p.services ?? []).reduce((acc, sv) => addItem(acc, itemOf(sv, p.operatorId, p.technicianId ?? null), p.now), { ...o, work });
}
/** Juntar um serviço ou uma peça (o técnico da ordem fica a ser quem o fez). */
export function addWorkItem(o, sv, by, now) {
    const w = openWork(o);
    return addItem(o, itemOf(sv, by, w.technicianId), now);
}
function openWork(o) {
    if (!o.work)
        throw new Error('Não é uma ordem de serviço');
    if (o.status !== 'open' || o.work.status === 'delivered' || o.work.status === 'cancelled')
        throw new Error(`A ordem ${o.work.number} já está ${WORK_LABEL[o.work.status].toLowerCase()}`);
    return o.work;
}
const withWork = (o, w, now) => ({ ...o, work: w, updatedAt: now.toISOString() });
/**
 * Em curso ou pronta. Só anda para a frente (várias caixas sem rede juntam-se
 * pelo estado mais avançado); o que faltar depois de pronta junta-se na mesma.
 */
export function setWorkStatus(o, status, by, now) {
    const w = openWork(o);
    if (WORK_RANK[status] <= WORK_RANK[w.status])
        return o;
    if (status === 'ready' && !billableItems(o).length && !w.quote)
        throw new Error('Junte o serviço feito antes de a dar como pronta');
    return withWork(o, { ...w, status, history: [...w.history, { status, at: now.toISOString(), by }] }, now);
}
/** Técnico, diagnóstico e data prometida. */
export function editWork(o, ch, now) {
    const w = openWork(o);
    if (ch.notes && ch.notes.length > 1000)
        throw new Error('Notas demasiado compridas');
    const items = ch.technicianId !== undefined
        ? o.items.map((i) => (i.paidIn || i.status === 'cancelled' || (i.staffId && i.staffId !== w.technicianId) ? i : { ...i, staffId: ch.technicianId, updatedAt: now.toISOString() }))
        : o.items;
    return { ...withWork(o, { ...w, ...ch }, now), items };
}
/** Linhas do orçamento (pró-forma): o que a ordem tem por cobrar. */
export function workLines(o) {
    return orderLines(o).map((l) => ({ ...l, description: l.serial ? `${l.description} — n.º série ${l.serial}` : l.description }));
}
/** Total por cobrar (com IVA). */
export const workTotal = (o) => (billableItems(o).length ? cartTotals(orderLines(o)).grossTotal : '0.00');
/** A pró-forma emitida fica como o orçamento da ordem, à espera da resposta do cliente. */
export function recordQuote(o, pf, now) {
    const w = openWork(o);
    return withWork(o, { ...w, quote: { number: pf.fullNumber, total: pf.grossTotal, at: now.toISOString(), approvedAt: null, refusedAt: null, by: null } }, now);
}
export function answerQuote(o, approved, by, now) {
    const w = openWork(o);
    if (!w.quote)
        throw new Error('Esta ordem não tem orçamento');
    const at = now.toISOString();
    return withWork(o, { ...w, quote: { ...w.quote, approvedAt: approved ? at : null, refusedAt: approved ? null : at, by } }, now);
}
/** O orçamento já não corresponde ao que está na ordem (juntaram-se ou tiraram-se coisas). */
export const quoteOutdated = (o) => !!o.work?.quote && workTotal(o) !== o.work.quote.total;
/** Entregue sem nada a cobrar (garantia, orçamento recusado): fecha sem documento. */
export function deliverWithoutCharge(o, by, now, reason) {
    const w = openWork(o);
    if (billableItems(o).length)
        throw new Error('Há serviços por cobrar: use "Entregar e cobrar" ou retire-os');
    if (!reason.trim())
        throw new Error('Diga porque se entrega sem cobrar');
    const at = now.toISOString();
    return { ...o, status: 'closed', closedAt: at, updatedAt: at, work: { ...w, status: 'delivered', notes: [w.notes, `Entregue sem cobrar: ${reason.trim()}`].filter(Boolean).join('\n'), history: [...w.history, { status: 'delivered', at, by }] } };
}
/** O cliente desistiu (antes de qualquer pagamento): fecha anulada. */
export function cancelWorkOrder(o, by, now, reason) {
    const w = openWork(o);
    if (!reason.trim())
        throw new Error('Indique o motivo da anulação');
    if (o.items.some((i) => i.paidIn))
        throw new Error('Há serviços já cobrados nesta ordem');
    const at = now.toISOString();
    return {
        ...o, status: 'cancelled', closedAt: at, updatedAt: at,
        work: { ...w, status: 'cancelled', notes: [w.notes, `Anulada: ${reason.trim()}`].filter(Boolean).join('\n'), history: [...w.history, { status: 'cancelled', at, by }] },
        items: o.items.map((i) => (i.status === 'cancelled' ? i : { ...i, status: 'cancelled', cancelReason: 'Ordem anulada', cancelledBy: by, updatedAt: at })),
    };
}
/** A cobrança fechou a comanda: a ordem fica entregue (na mesma transacção da venda). */
export function deliveredBy(o, by, at) {
    if (!o.work || o.status !== 'closed' || o.work.status === 'delivered')
        return o;
    return { ...o, work: { ...o.work, status: 'delivered', history: [...o.work.history, { status: 'delivered', at, by }] } };
}
/** Mensagem de "pronta" para o WhatsApp. */
export function readyMessage(o, company) {
    const w = o.work;
    const total = workTotal(o);
    return `Olá ${w.customer.name}, a sua ordem ${w.number} (${w.object}) está pronta para levantar em ${company}.${total !== '0.00' ? ` Valor: ${total.replace('.', ',')} MT.` : ''}`;
}
/** Ordens em atraso: prometidas para antes de hoje e ainda não prontas. */
export const workLate = (o, today) => !!o.work?.promisedOn && o.work.promisedOn < today && WORK_RANK[o.work.status] < WORK_RANK.ready;
//# sourceMappingURL=services.js.map